<template>
    <div class="relative" :class="props.wrapperClass">
        <div :class="props.labelInputWrapperClass">
            <slot name="label">
                <label
                    :for="props.id"
                    class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                    :class="options.labelClass + srOnly"
                >
                    {{ props.label }}
                    <span
                        class="text-red-500"
                        aria-hidden="true"
                        v-if="options.required"
                    >
                        *
                    </span>
                    <span
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal"
                        v-if="options.optional"
                    >
                        (optional)
                    </span>
                </label>
                <div v-if="options.fakeLabelSpace" class="h-[25px]"></div>
            </slot>
            <div class="relative">
                <slot name="input">
                    <textarea
                        :id="props.id"
                        :name="props.name"
                        :placeholder="props.placeholder"
                        class="block w-full bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder-slate-500 placeholder:text-sm text-sm transition-colors focus:ring-0 focus:border-slate-800 dark:focus:border-slate-500 hover:border-slate-800 dark:hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        :class="options.inputClass + ' ' + inputError"
                        :disabled="props.disabled"
                        v-model="inputValue"
                        @input="emit('onInput', $event.target)"
                        @blur="emit('onBlur', $event.target)"
                        @focus="emit('onFocus', $event.target)"
                        @change="emit('onChange', $event.target)"
                        v-debounce="debounceTime"
                        :autofocus="props.autofocus"
                    ></textarea>
                </slot>
            </div>
        </div>
        <slot name="error">
            <transition name="fade">
                <div
                    class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                    role="alert"
                    v-if="componentError"
                >
                    <Icon icon="caution" />
                    {{ componentError }}
                </div>
            </transition>
        </slot>
        <slot name="hint">
            <div class="text-slate-600 dark:text-slate-400 text-xs py-1.5" v-if="props.hint">
                {{ props.hint }}
            </div>
        </slot>
    </div>
</template>
<script setup>
import { ref } from 'vue';
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const inputValue = defineModel('inputValue');

const props = defineProps({
    name: { type: String },
    label: { type: String, required: true },
    id: { type: String, required: true },
    placeholder: { type: String, default: '' },
    step: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    hint: { type: [String, null], default: null },
    errors: { type: Object, default: null },
    error: { type: [String, null], default: null },
    options: { type: Object, default: null },
    autofocus: { type: Boolean, default: false },
});

const validOptionKeys = [
    'optional', // Adds optional part of label
    'required', // Marks the label with a required asterisk
    'wrapperClass', // Covers the entire component
    'labelInputWrapperClass', // Covers the label and input
    'labelClass', // Covers the label
    'inputClass', // Covers the input
    'hideLabel', // Applies sr-only class to the label
    'icon', // Icon name for input field ( building, search... )
    'iconPosition', // Icon position for input field. left | right
    'debounceTime', // Debounce time for input field
    'toggleHidden', // (bool) Toggle password visibility
    'fakeLabelSpace', // (bool) Adds a fake label space
];

const settings = {
    optional: false,
    required: false,
    wrapperClass: '',
    labelInputWrapperClass: '',
    labelClass: '',
    inputClass: '',
    hideLabel: false,
    debounceTime: 200,
    toggleHidden: false,
    vaildatePassword: false,
    fakeLabelSpace: false,
};

const showHidden = ref(false);

// Events will return $event.target.
// If value is needed use [el].value on the method callback of the component.
// @onblur(handleOnBlur) ... handleOnBlur(el) { ... el.value }
const emit = defineEmits(['onInput', 'onFocus', 'onBlur', 'onChange']);

// Merge default settings and props.options.
// props.options takes precedence.
const options = computed(() => {
    if (props.options) {
        for (const key in props.options) {
            if (!validOptionKeys.includes(key)) {
                throw new Error(`Invalid config key: ${key}`);
            }
        }
    }

    return { ...settings, ...props.options };
});

/**
 *  Pass in props `error` or `errors`.
 *  `error` is priority.
 *  errors[key name] comes from laravel backend validation.
 *  error="Please enter a valid email address."
 *  :errors="{ email: "Please enter a valid email address.", name: "Please enter a name" }"
 *  When `errors` is used it will only use the matching key in the object.
 */
const componentError = computed(() => {
    if (props.error) {
        return props.error;
    }

    if (props.errors && props.errors[props.name]) {
        return props.errors[props.name][0];
    }

    return null;
});

const srOnly = computed(() => {
    return options.value.hideLabel ? ' sr-only' : '';
});

const inputError = computed(() => {
    return componentError.value ? ' !border-red-500 ' : '';
});

const debounceTime = computed(() => {
    return options.value.debounceTime;
});
</script>
